import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, errorMessage } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { track } from "../lib/analytics";
import { FEE_DEFAULT } from "../lib/appDefaults";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import { UserCheck, Crown, Users, ArrowRight } from "lucide-react";

export default function InviteAccept() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const { user, loading, refresh, config } = useAuth();
  const fee = config?.entry_fee ?? FEE_DEFAULT;
  const [invite, setInvite] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setErr("Missing invitation token"); return; }
    (async () => {
      try {
        const { data } = await api.get(`/invite/${token}`);
        setInvite(data);
      } catch (e) {
        setErr(errorMessage(e));
      }
    })();
  }, [token]);

  const accept = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/invite/${token}/accept`);
      toast.success("You're in!");
      track("team_joined", { funnel_step: 4, action: "invite_accept" });
      await refresh();
      navigate("/team/dashboard");
    } catch (e) {
      toast.error(errorMessage(e));
    } finally { setBusy(false); }
  };
  const decline = async () => {
    setBusy(true);
    try {
      await api.post(`/invite/${token}/decline`);
      toast.success("Invitation declined");
      navigate("/");
    } catch (e) {
      toast.error(errorMessage(e));
    } finally { setBusy(false); }
  };

  if (err) {
    return (
      <div className="App"><Navbar />
        <div className="flex items-start justify-center px-6 pt-12 pb-16 sm:pt-16 sm:pb-20">
          <div className="card-surface max-w-md w-full text-center" data-testid="invite-error">
            <h1 className="font-display text-2xl text-white">Invitation Invalid</h1>
            <p className="text-gray-400 mt-3">{err}</p>
            <Link to="/" className="btn-secondary mt-6 inline-block">Back to home</Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!invite || loading) {
    return (<div className="App"><Navbar /><div className="min-h-[60vh] flex items-center justify-center"><span className="spinner" /></div></div>);
  }

  // not logged in or wrong email or not paid scenarios
  const notLoggedIn = !user;
  const wrongEmail = user && user.college_email && user.college_email.toLowerCase() !== invite.invited_email.toLowerCase();
  const notPaid = user && user.payment_status !== "paid";

  return (
    <div className="App" data-testid="invite-accept-page">
      <Navbar />
      <div className="flex items-start justify-center px-6 pt-12 pb-16 sm:pt-16 sm:pb-20">
        <div className="w-full max-w-lg fade-up">
          <div className="label-eyebrow mb-3">Team Invitation</div>
          <h1 className="font-display text-4xl text-white">You're invited.</h1>

          <div className="mt-8 card-surface border-l-2 border-brand">
            <div className="flex items-center gap-3 text-sm text-gray-400">
              <Crown className="w-4 h-4 text-brand" strokeWidth={1.5} />
              <span><span className="text-white font-medium">{invite.leader_name}</span> invited <span className="text-brand">{invite.invited_email}</span></span>
            </div>
            <div className="mt-5">
              <div className="label-eyebrow">Team</div>
              <div className="font-display text-3xl text-white mt-1" data-testid="invite-team-name">{invite.team_name}</div>
            </div>
            <div className="mt-5 flex items-center gap-2 text-sm text-gray-400">
              <Users className="w-4 h-4" strokeWidth={1.5} /> Currently <span className="text-white">{invite.member_count}</span> member{invite.member_count === 1 ? "" : "s"} on board
            </div>
          </div>

          {notLoggedIn && (
            <div className="mt-6 p-4 border border-[#F59E0B]/40 bg-[#F59E0B]/5">
              <div className="text-sm text-gray-300">You must <strong className="text-white">register and pay ₹{fee}</strong> before accepting.</div>
              <Link to={`/register?prefill=${encodeURIComponent(invite.invited_email)}`} className="btn-primary mt-3 inline-flex items-center gap-2" data-testid="invite-register">
                Register / Login <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          )}

          {wrongEmail && (
            <div className="mt-6 p-4 border border-[#EF4444]/40 bg-[#EF4444]/5" data-testid="invite-wrong-email">
              <div className="text-sm text-gray-300">
                This invitation is for <strong className="text-white">{invite.invited_email}</strong> but you're logged in as <strong className="text-white">{user.college_email}</strong>. Please log out and use the correct account.
              </div>
            </div>
          )}

          {!notLoggedIn && !wrongEmail && notPaid && (
            <div className="mt-6 p-4 border border-[#F59E0B]/40 bg-[#F59E0B]/5">
              <div className="text-sm text-gray-300">You need to complete your <strong className="text-white">₹{fee} registration payment</strong> first.</div>
              <Link to="/payment" className="btn-primary mt-3 inline-flex items-center gap-2" data-testid="invite-pay">Pay Now <ArrowRight className="w-4 h-4" /></Link>
            </div>
          )}

          {!notLoggedIn && !wrongEmail && !notPaid && (
            <div className="mt-6 flex flex-wrap gap-3" data-testid="invite-actions">
              <button onClick={accept} disabled={busy} className="btn-primary flex items-center gap-2" data-testid="invite-accept-btn">
                {busy ? <span className="spinner" /> : <UserCheck className="w-4 h-4" />} Accept Invitation
              </button>
              <button onClick={decline} disabled={busy} className="btn-secondary" data-testid="invite-decline-btn">Decline</button>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
